import React, { useState, useRef, useEffect } from 'react';
import {
    X, Mic, Image as ImageIcon, Type, Send, Loader,
    Check, AlertTriangle, Play, Square, RefreshCw, Copy, MessageCircle,
    Plus, Search
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AiOrderModal = ({ onClose, onAddItems }) => {
    const [mode, setMode] = useState('text'); // text, audio, image
    const [isLoading, setIsLoading] = useState(false);
    const [inputText, setInputText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [parsedResult, setParsedResult] = useState(null); // { matches: [], transcription: '' }
    const [showWhatsAppPreview, setShowWhatsAppPreview] = useState(false);
    const [customSuggestion, setCustomSuggestion] = useState('');
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [availableProducts, setAvailableProducts] = useState([]);

    // Helper: Convertir markdown de WhatsApp a HTML
    const renderWhatsAppMarkdown = (text) => {
        // Convertir *texto* a <strong>texto</strong>
        return text
            .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br/>');
    };

    // Audio Refs
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    // Cargar productos cuando se abre el buscador
    useEffect(() => {
        if (showProductSearch && availableProducts.length === 0) {
            console.log('🔍 Cargando productos...');

            // NOTA: include_services=true permite buscar servicios como DOMICILIOS (no inventariables)
            fetch('/api/products?is_active=1&pageSize=1000&include_services=true', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            })
                .then(res => {
                    console.log('📡 Respuesta del API:', res.status);
                    if (!res.ok) {
                        throw new Error(`HTTP error! status: ${res.status}`);
                    }
                    return res.json();
                })
                .then(response => {
                    console.log('📦 Respuesta completa:', response);
                    // CORREGIDO: El backend retorna {success, data, pagination}
                    // Los productos están en response.data, NO en response.products
                    if (response.success && response.data) {
                        console.log(`✅ ${response.data.length} productos cargados`);
                        setAvailableProducts(response.data);
                    } else {
                        console.warn('⚠️ Estructura de respuesta inesperada:', response);
                        toast.error('Error: respuesta del servidor no válida');
                    }
                })
                .catch(err => {
                    console.error('❌ Error loading products:', err);
                    toast.error('Error al cargar productos: ' + err.message);
                });
        }
    }, [showProductSearch]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioUrl = URL.createObjectURL(audioBlob);
                setPreviewUrl(audioUrl);
                // Create file from blob
                const file = new File([audioBlob], "recording.webm", { type: 'audio/webm' });
                setSelectedFile(file);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err) {
            console.error(err);
            toast.error('No se pudo acceder al micrófono');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            // Stop tracks
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    // Listen for paste events
    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData.items;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    setSelectedFile(blob);
                    setPreviewUrl(URL.createObjectURL(blob));
                    setMode('image');
                    toast.success('Imagen pegada del portapapeles');
                    break;
                }
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, []);

    const handleSubmit = async () => {
        if (mode === 'text' && !inputText.trim()) return toast.error('Escribe algo');
        if (mode !== 'text' && !selectedFile) return toast.error('Selecciona o graba algo');

        setIsLoading(true);
        const formData = new FormData();
        formData.append('mode', mode);

        if (mode === 'text') {
            formData.append('text', inputText);
        } else {
            formData.append('file', selectedFile);
        }

        try {
            // Adjust URL if needed based on proxy setup, using relative path usually works if created-react-app proxy is set
            // assuming axios baseURL is set or relative path works.
            const token = localStorage.getItem('token');
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            };

            const res = await axios.post(`${process.env.REACT_APP_API_URL || '/api'}/ai/parse-order`, formData, config);

            if (res.data.success) {
                setParsedResult(res.data);
                toast.success('Procesado con éxito');
            } else {
                toast.error(res.data.message || 'Error al procesar');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error de conexión con IA');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirm = () => {
        // Filter valid matches and send to parent
        if (!parsedResult) return;
        const validItems = parsedResult.matches
            // Modificado: Permitir stock <= 0 si es un servicio agregado manualmente
            .filter(m => m.matched_product && m.quantity > 0 && (m.matched_product.stock > 0 || m.notes === 'Agregado manualmente'))
            .map(m => ({
                id: m.matched_product.id,
                quantity: m.quantity,
                product_name: m.matched_product.name,
                price: m.matched_product.price,
                stock: m.matched_product.stock,
                internal_code: m.matched_product.internal_code,
                // Add other necessary fields for cart logic
            }));

        // Modificado: Solo mostrar error si no hay productos válidos NI servicios agregados
        if (validItems.length === 0 && parsedResult.matches.some(m => m.matched_product && m.matched_product.stock <= 0 && m.notes !== 'Agregado manualmente')) {
            toast.error('No se pueden agregar productos sin stock');
            return;
        }

        onAddItems(validItems);
        onClose();
    };

    const copyWhatsAppResponseLogic = () => {
        if (!parsedResult) return '';

        const identified = parsedResult.matches.filter(m => m.matched_product);
        const available = identified.filter(m => m.matched_product.stock > 0 || m.notes === 'Agregado manualmente');
        const outOfStock = identified.filter(m => m.matched_product.stock <= 0 && m.notes !== 'Agregado manualmente');
        const notFound = parsedResult.matches.filter(m => !m.matched_product);

        const formatPrice = (price) => {
            return '$' + Math.round(price || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        };

        const productsByCategory = {
            'PERLA': [],
            'SIROPE': [],
            'AZUCAR': [],
            'JELY': [],
            'GOMAS': [],
            'OTRO': []
        };

        let totalValue = 0;

        available.forEach(m => {
            const unitPrice = m.matched_product.price || 0;
            const quantity = m.quantity || 1;
            const subtotal = quantity * unitPrice;
            totalValue += subtotal;

            const productName = m.matched_product.name.toLowerCase();
            let category = 'OTRO';

            if (productName.includes('perla') || productName.includes('liquipop') || productName.includes('bubols')) {
                category = 'PERLA';
            } else if (productName.includes('sirope') || productName.includes('salsa')) {
                category = 'SIROPE';
            } else if (productName.includes('azucar') || productName.includes('skarcha')) {
                category = 'AZUCAR';
            } else if (productName.includes('jely') || productName.includes('jeli') || productName.includes('yexis')) {
                category = 'JELY';
            } else if (productName.includes('goma')) {
                category = 'GOMAS';
            }

            let line = quantity === 1
                ? `- ${quantity}x ${m.matched_product.name}: ${formatPrice(subtotal)}`
                : `- ${quantity}x ${m.matched_product.name}: ${formatPrice(subtotal)} (${formatPrice(unitPrice)} c/u)`;

            productsByCategory[category].push(line);
        });

        let message = `*Cotización de Pedido - Topping Frozen 🍦*\n`;
        message += `--------------------------\n\n`;

        if (available.length > 0) {
            message += `✅ *Productos Confirmados:*\n\n`;

            const categoryConfig = {
                'PERLA': { emoji: '🔵', name: '*PERLAS EXPLOSIVAS*' },
                'SIROPE': { emoji: '🟣', name: '*SIROPES Y SALSAS*' },
                'AZUCAR': { emoji: '⚪', name: '*AZÚCARES*' },
                'JELY': { emoji: '🟢', name: '*JELIES*' },
                'GOMAS': { emoji: '🍬', name: '*GOMAS*' },
                'OTRO': { emoji: '🟡', name: '*OTROS PRODUCTOS*' }
            };

            Object.keys(productsByCategory).forEach(category => {
                if (productsByCategory[category].length > 0) {
                    const config = categoryConfig[category];
                    message += `${config.emoji} ${config.name}\n`;
                    productsByCategory[category].forEach(line => { message += line + '\n'; });
                    message += '\n';
                }
            });
        }

        if (outOfStock.length > 0) {
            message += `🚫 *PRODUCTOS SIN STOCK / AGOTADOS:*\n`;
            outOfStock.forEach(m => {
                message += `- ${m.matched_product.name} (Agotado momentáneamente) ❌\n`;
            });
            message += `\n¿Deseas cambiar alguno de estos productos por una de nuestras alternativas disponibles? 👇\n\n`;

            // Sugerencias inteligentes para Gomas (Psicología de ventas)
            outOfStock.filter(m => m.matched_product.name.toLowerCase().includes('goma')).forEach(m => {
                const name = m.matched_product.name;
                const flavor = name.split(' ')[1] || 'de este sabor';

                if (name.includes('3000')) {
                    message += `💡 *Sugerencia para ${flavor}*: \n`;
                    message += `- ¿Prefieres las mismas *${flavor} pero en presentación de 1000g*? \n`;
                    message += `- ¿O tal vez te gustaría probar *otros sabores en 3000g* que sí tenemos (Gusanos, Estrellas, Corazones)? 😊\n\n`;
                } else if (name.includes('1000')) {
                    message += `💡 *Sugerencia para ${flavor}*: \n`;
                    message += `- ¿Te gustaría llevar las *${flavor} de 3000g* (mejor precio por gramo)? \n`;
                    message += `- ¿O prefieres otros sabores disponibles en presentación de 1000g? 😊\n\n`;
                }
            });
        }

        if (notFound.length > 0) {
            message += `❓ *No logré identificar estos productos:*\n`;
            notFound.forEach(m => {
                message += `- "${m.original_term}" x ${m.quantity || 1}\n`;
            });
            message += `\n¿Me podrías confirmar si son sabores especiales o de alguna marca específica?\n\n`;
        }

        if (available.length > 0) {
            message += `--------------------------\n`;
            message += `💰 *VALOR TOTAL: ${formatPrice(totalValue)}*\n`;
            message += `--------------------------\n\n`;
        }

        if (customSuggestion.trim()) {
            message += `${customSuggestion}\n`;
        } else {
            const hasGomasAgotadas = outOfStock.some(m => m.matched_product.name.toLowerCase().includes('goma'));

            // Si ya sugerimos gomas arriba, no saturamos con lo genérico
            if (!hasGomasAgotadas) {
                message += `✨ *¿Te gustaría complementar tu pedido?*\n`;
                const hasPerlas = available.some(m => m.matched_product.name.toLowerCase().includes('perla') || m.matched_product.name.toLowerCase().includes('bubols'));
                const hasSiropes = available.some(m => m.matched_product.name.toLowerCase().includes('sirope'));

                if (hasPerlas && !hasSiropes) {
                    message += `- Nuestros siropes Geniality combinan perfecto con tus perlas. ¿Te gustaría agregar alguno? 🍓\n\n`;
                } else if (hasSiropes && !hasPerlas) {
                    message += `- ¿Qué tal unas Perlas Liquipops de 350g o 1100g para acompañar tus siropes? 🔴\n\n`;
                } else {
                    message += `- Contamos con Siropes Neón y Perlas Liquipops en diferentes presentaciones. 🌈\n\n`;
                }
            }
        }

        message += `\n¡Quedo atento a tu respuesta para procesar la factura! 🚀`;
        return message;
    };

    const generateWhatsAppMessage = () => {
        if (!parsedResult) return '';
        return copyWhatsAppResponseLogic();  // Usar la lógica ya existente
    };

    const copyWhatsAppResponse = () => {
        const message = copyWhatsAppResponseLogic();

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(message).then(() => {
                toast.success('Cotización copiada al portapapeles');
            });
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = message;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success('Cotización copiada');
            } catch (err) {
                console.error('Bummer', err);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleQuantityChange = (index, value) => {
        const newMatches = [...parsedResult.matches];
        newMatches[index].quantity = parseInt(value) || 0;
        setParsedResult({ ...parsedResult, matches: newMatches });
    };

    const handlePriceChange = (index, value) => {
        const newMatches = [...parsedResult.matches];
        newMatches[index].matched_product.price = parseInt(value);
        setParsedResult({ ...parsedResult, matches: newMatches });
    };

    const handleRemoveItem = (index) => {
        const newMatches = parsedResult.matches.filter((_, idx) => idx !== index);
        setParsedResult({ ...parsedResult, matches: newMatches });
        toast.success('Producto eliminado de la lista');
    };

    const handleAddProduct = (product) => {
        if (!parsedResult) return;

        const newMatch = {
            original_term: product.product_name,
            quantity: 1,
            matched_product: {
                ...product,
                name: product.product_name,
                price: product.standard_price,
                active: product.is_active === 1
            },
            alternatives: [],
            notes: 'Agregado manualmente'
        };

        setParsedResult({
            ...parsedResult,
            matches: [...parsedResult.matches, newMatch]
        });

        setShowProductSearch(false);
        setProductSearchTerm('');
        toast.success(`${product.product_name} agregado`);
    };

    const reset = () => {
        setParsedResult(null);
        setInputText('');
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        ✨ Asistente IA
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {!parsedResult ? (
                        <div className="space-y-6">
                            {/* Tabs */}
                            <div className="flex p-1 bg-gray-100 rounded-lg">
                                {[
                                    { id: 'text', icon: Type, label: 'Texto' },
                                    { id: 'image', icon: ImageIcon, label: 'Imagen' },
                                    { id: 'audio', icon: Mic, label: 'Audio' }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => { setMode(tab.id); setSelectedFile(null); setPreviewUrl(null); }}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${mode === tab.id ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Input Area */}
                            <div className="min-h-[200px] flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 p-6">
                                {mode === 'text' && (
                                    <textarea
                                        className="w-full h-40 bg-transparent border-none focus:ring-0 text-gray-700 placeholder-gray-400 resize-none text-lg"
                                        placeholder="Ej: Quiero 2 cajas de chocolate y 3 bolsas de crema de leche..."
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        autoFocus
                                    />
                                )}

                                {mode === 'image' && (
                                    <div className="text-center w-full">
                                        {previewUrl ? (
                                            <div className="relative inline-block">
                                                <img src={previewUrl} alt="Preview" className="max-h-60 rounded-lg shadow-md" />
                                                <button
                                                    onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow hover:bg-red-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    id="img-upload"
                                                    onChange={handleFileChange}
                                                />
                                                <label
                                                    htmlFor="img-upload"
                                                    className="cursor-pointer flex flex-col items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors"
                                                >
                                                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-2">
                                                        <ImageIcon className="w-8 h-8 text-blue-500" />
                                                    </div>
                                                    <span className="font-medium">Sube una foto o tómala</span>
                                                    <span className="text-sm opacity-75">Listas a mano, fotos de productos...</span>
                                                </label>
                                            </>
                                        )}
                                    </div>
                                )}

                                {mode === 'audio' && (
                                    <div className="text-center">
                                        {previewUrl ? (
                                            <div className="w-full max-w-xs mx-auto">
                                                <audio src={previewUrl} controls className="w-full mb-4" />
                                                <button
                                                    onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                                                    className="text-red-500 text-sm hover:underline"
                                                >
                                                    Grabar de nuevo
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={isRecording ? stopRecording : startRecording}
                                                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${isRecording
                                                    ? 'bg-red-100 ring-4 ring-red-50 animate-pulse'
                                                    : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:scale-105'
                                                    }`}
                                            >
                                                {isRecording ? (
                                                    <Square className="w-8 h-8 text-red-600" fill="currentColor" />
                                                ) : (
                                                    <Mic className="w-8 h-8 text-white" />
                                                )}
                                            </button>
                                        )}
                                        <p className="mt-4 text-gray-600 font-medium">
                                            {isRecording ? 'Grabando... (Click para parar)' : previewUrl ? 'Listo para enviar' : 'Click para grabar'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Results View
                        <div className="space-y-4">
                            {parsedResult.transcription && (
                                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 italic">
                                    "{parsedResult.transcription}"
                                </div>
                            )}

                            <h3 className="font-bold text-gray-700 mb-2">Productos Identificados</h3>
                            <div className="space-y-2">
                                {parsedResult.matches.map((item, idx) => (
                                    <div key={idx} className={`p-3 rounded-lg border flex items-center justify-between group ${item.matched_product ? 'bg-white border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                {item.matched_product ? (
                                                    (item.matched_product.stock <= 0 && item.notes !== 'Agregado manualmente') ? (
                                                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                                                    ) : (
                                                        <Check className="w-4 h-4 text-green-600" />
                                                    )
                                                ) : (
                                                    <AlertTriangle className="w-4 h-4 text-red-500" />
                                                )}
                                                <p className="font-medium text-gray-900">
                                                    {item.matched_product ? item.matched_product.name : `No encontrado: "${item.original_term}"`}
                                                </p>
                                                {item.matched_product && (
                                                    <div className="flex gap-1 mt-1">
                                                        {/* Solo mostrar stock para productos inventariables (stock > 0) */}
                                                        {item.matched_product.stock > 0 && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-green-100 text-green-700">
                                                                Stock: {item.matched_product.stock}
                                                            </span>
                                                        )}
                                                        {/* Identificar servicios (stock <= 0 pero es producto válido) */}
                                                        {item.matched_product.stock <= 0 && item.notes === 'Agregado manualmente' && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-blue-100 text-blue-700">
                                                                SERVICIO
                                                            </span>
                                                        )}
                                                        {item.matched_product.is_local && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-blue-100 text-blue-700">
                                                                ESPECIAL
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {item.notes && <p className="text-xs text-gray-500 ml-6">{item.notes}</p>}
                                        </div>

                                        {/* Controles de cantidad y precio - mostrar para productos con stock O servicios */}
                                        {item.matched_product && (item.matched_product.stock > 0 || item.notes === 'Agregado manualmente') && (
                                            <div className="flex items-center gap-3">
                                                {/* Para servicios: Select de precio editable */}
                                                {item.notes === 'Agregado manualmente' && item.matched_product.stock <= 0 ? (
                                                    <select
                                                        value={item.matched_product.price}
                                                        onChange={(e) => handlePriceChange(idx, e.target.value)}
                                                        className="text-sm font-bold text-gray-700 border rounded px-2 py-1 bg-white focus:ring-2 focus:ring-blue-400"
                                                    >
                                                        {[14000, 15000, 16000, 17000, 18000, 19000, 20000, 21000, 22000, 23000, 24000].map(price => (
                                                            <option key={price} value={price}>
                                                                ${price.toLocaleString()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="text-sm font-bold text-gray-600">
                                                        ${item.matched_product.price?.toLocaleString()}
                                                    </span>
                                                )}
                                                <div className="flex items-center border rounded bg-white">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={item.quantity}
                                                        onChange={(e) => handleQuantityChange(idx, e.target.value)}
                                                        className="w-12 text-center text-sm p-1 border-none focus:ring-0"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Mensaje de error solo para productos SIN stock que NO son servicios */}
                                        {item.matched_product && item.matched_product.stock <= 0 && item.notes !== 'Agregado manualmente' && (
                                            <div className="flex flex-col items-end gap-1">
                                                <div className="text-xs font-bold text-red-500 flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" /> No disponible
                                                </div>
                                                {item.alternatives && item.alternatives.length > 0 && (
                                                    <div className="text-[10px] text-blue-600 font-medium">
                                                        Sug: {item.alternatives.map(a => a.name).slice(0, 1).join(', ')}...
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Botón para eliminar item */}
                                        <button
                                            onClick={() => handleRemoveItem(idx)}
                                            className="ml-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Eliminar producto"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {parsedResult.matches.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No se encontraron productos. Intenta ser más específico.
                                </div>
                            )}

                            {/* Botón y búsqueda para agregar productos manualmente */}
                            <div className="mt-4 border-t pt-4">
                                {!showProductSearch ? (
                                    <button
                                        onClick={() => setShowProductSearch(true)}
                                        className="w-full py-2 px-4 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors border border-blue-200"
                                    >
                                        <Plus className="w-4 h-4" /> Agregar Producto Manualmente
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <div className="flex-1 relative">
                                                <input
                                                    type="text"
                                                    value={productSearchTerm}
                                                    onChange={(e) => setProductSearchTerm(e.target.value)}
                                                    placeholder="Buscar producto por nombre..."
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    autoFocus
                                                />
                                                <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setShowProductSearch(false);
                                                    setProductSearchTerm('');
                                                }}
                                                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>

                                        {/* Lista de productos filtrados */}
                                        {productSearchTerm.length >= 2 && (
                                            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                                                {(() => {
                                                    console.log('🔎 Filtrando productos...');
                                                    console.log('📋 Total productos disponibles:', availableProducts.length);
                                                    console.log('🔍 Término de búsqueda:', productSearchTerm);

                                                    const filtered = availableProducts.filter(p => {
                                                        const matchesName = p.product_name?.toLowerCase().includes(productSearchTerm.toLowerCase());
                                                        const isActive = p.is_active === 1;
                                                        // NO filtrar por stock - el backend ya maneja servicios con include_services=true

                                                        return matchesName && isActive;
                                                    });

                                                    console.log('✅ Productos filtrados:', filtered.length);

                                                    return filtered.slice(0, 10).map(product => {
                                                        const isService = product.stock <= 0;
                                                        return (
                                                            <button
                                                                key={product.id}
                                                                onClick={() => handleAddProduct(product)}
                                                                className="w-full px-3 py-2 hover:bg-blue-50 text-left border-b last:border-b-0 transition-colors flex items-center justify-between gap-2"
                                                            >
                                                                <span className="font-medium text-gray-900 text-sm flex-1 truncate">
                                                                    {product.product_name}
                                                                    {isService && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded">Servicio</span>}
                                                                </span>
                                                                {!isService && (
                                                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                        Stock: {product.stock}
                                                                    </span>
                                                                )}
                                                                <span className="text-sm font-bold text-gray-600 whitespace-nowrap">
                                                                    ${product.standard_price?.toLocaleString()}
                                                                </span>
                                                            </button>
                                                        );
                                                    })
                                                })()}
                                                {availableProducts.filter(p =>
                                                    p.product_name?.toLowerCase().includes(productSearchTerm.toLowerCase()) &&
                                                    p.is_active === 1
                                                    // NO filtrar por stock aquí, el backend ya filtró lo necesario
                                                ).length === 0 && (
                                                        <div className="p-4 text-center text-gray-500 text-sm">
                                                            No se encontraron productos
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                        {productSearchTerm.length < 2 && (
                                            <p className="text-xs text-gray-500 text-center">
                                                Escribe al menos 2 caracteres para buscar
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* WhatsApp Message Preview (Renderizado) */}
                {parsedResult && parsedResult.matches.some(m => m.matched_product) && (
                    <div className="px-4 py-3 bg-green-50 border-t border-green-200">
                        <button
                            onClick={() => setShowWhatsAppPreview(!showWhatsAppPreview)}
                            className="text-sm font-semibold text-green-700 flex items-center gap-2 hover:text-green-800"
                        >
                            <MessageCircle className="w-4 h-4" />
                            {showWhatsAppPreview ? '▼' : '▶'} Vista Previa WhatsApp
                        </button>
                        {showWhatsAppPreview && (
                            <>
                                {/* Campo editable para mensaje de sugerencia */}
                                <div className="mt-3">
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                                        💬 Mensaje de Sugerencia (Editable):
                                    </label>
                                    <textarea
                                        value={customSuggestion}
                                        onChange={(e) => setCustomSuggestion(e.target.value)}
                                        placeholder="✨ ¿Te gustaría complementar tu pedido?&#10;- Contamos con Siropes Neón y Perlas Liquipops. 🌈"
                                        className="w-full p-3 text-sm border border-green-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono"
                                        rows={4}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        💡 Deja vacío para usar la sugerencia automática basada en los productos.
                                    </p>
                                </div>

                                {/* Vista Previa del Mensaje Completo */}
                                <div className="mt-3">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">Vista Previa:</p>
                                    <div
                                        className="p-4 bg-white rounded-lg border border-green-300 text-sm font-mono whitespace-pre-wrap"
                                        dangerouslySetInnerHTML={{ __html: renderWhatsAppMarkdown(generateWhatsAppMessage()) }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-between rounded-b-2xl">
                    {parsedResult ? (
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={reset}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg flex items-center gap-2"
                            >
                                <RefreshCw className="w-4 h-4" /> Resetear
                            </button>
                            <div className="flex-1"></div>
                            <button
                                onClick={copyWhatsAppResponse}
                                className="px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg flex items-center gap-2 font-bold transition-all border border-blue-200"
                            >
                                <MessageCircle className="w-4 h-4" /> Respuesta WhatsApp
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow flex items-center gap-2 font-bold disabled:opacity-50"
                                // Modificado: Habilitar si hay productos con stock O servicios agregados manualmente
                                disabled={!parsedResult.matches.some(m => m.matched_product && (m.matched_product.stock > 0 || m.notes === 'Agregado manualmente'))}
                            >
                                <Check className="w-4 h-4" /> Agregar Confirmados
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading || (mode !== 'text' && !selectedFile) || (mode === 'text' && !inputText)}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-bold transition-all"
                        >
                            {isLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            {isLoading ? 'Procesando...' : 'Procesar Orden'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiOrderModal;
